import { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, CardActionArea, Chip, Grid,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { faSpotify } from '@fortawesome/free-brands-svg-icons';
import { getImageSmallUrl } from '@sda/utils/image';
import { getRatingString } from '@sda/utils/string';
import { ProfileBadge } from '@client/components/common/ProfileBadge';
import { Clamp } from '@client/components/common/Clamp';
import { Media } from '@client/components/common/Media';
import { FontAwesomeIcon } from '@client/components/common/FontAwesomeIcon';
import { INTERPUNCT } from '@client/components/common/constants';
import type { RequireAtLeastOne } from '@sda/helpers/helpers.types';
import type { CardActionAreaProps } from '@mui/material';
import type { PublicImageUrl } from '@sda/interfaces/image';

const useStyles = makeStyles({ name: 'ConnectCard' })((theme) => ({
  root: {
    borderRadius: theme.radii.large,
    position: 'relative',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },

    backgroundColor: 'transparent',
    boxShadow: 'none',
    overflow: 'visible',
  },
  media: {
    marginBottom: theme.spacing(1.5),
    top: 1,
    borderRadius: theme.radii.large,
    overflow: 'hidden',
  },
  mediaContainer: {
    position: 'relative',
    width: '100%',

    '&:hover > div:first-of-type': {
      boxShadow: theme.shadows[5],
    },
  },
  titleContainer: {
    flex: 1,
  },
  favoriteIcon: {
    fontSize: 15,
    marginRight: theme.spacing(1 / 2),
  },
  chipContainer: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}));

interface ConnectDestinationProps {
  readonly path?: string;
  readonly onClick?: () => void;
}

type ConnectDestination = RequireAtLeastOne<ConnectDestinationProps, 'onClick' | 'path'>;

interface ConnectCardPropsWithoutDestination {
  readonly imgSrc?: string | PublicImageUrl[] | string[];
  readonly title: string;
  readonly numReviews: number;
  readonly score: number;
  readonly location?: string | React.ReactNode;
  readonly dateRange: string;
  readonly badge?: string;
  readonly isFeatured?: boolean;
  readonly spotifyMonthlyActiveListeners?: string;
  readonly containerClassName?: string;
  readonly chipLabel?: string;
}

export type ConnectCardProps = ConnectCardPropsWithoutDestination & ConnectDestination;

export const ConnectCard = memo<ConnectCardProps & CardActionAreaProps>(({
  imgSrc, path, title, numReviews,
  score, location, dateRange, badge,
  containerClassName = '', chipLabel, onClick = () => {},
  isFeatured, spotifyMonthlyActiveListeners, ...restProps
}) => {
  const { classes, cx } = useStyles();

  const cardActionAreaProps = path ? {
    component: Link,
    to: path,
  } : { onClick };

  return (
    <Card className={cx(classes.root, containerClassName)}>
      <CardActionArea
        className={classes.mediaContainer}
        {...cardActionAreaProps}
        {...restProps}
      >
        {Boolean(imgSrc) && (
          <Media
            className={classes.media}
            images={imgSrc}
            sizeCallback={getImageSmallUrl}
            title={title}
            enableCarousel
          />
        )}

        {Boolean(chipLabel) && (
          <div className={classes.chipContainer}>
            <Chip
              size="small"
              color="info"
              label={chipLabel}
            />
          </div>
        )}

        {(Boolean(badge) || Boolean(isFeatured)) && (
          <ProfileBadge
            isFeatured={isFeatured}
            badgeId={badge}
          />
        )}

        <div>
          <Grid
            container
            spacing={0}
          >
            <Grid
              item
              container
              spacing={1}
              justifyContent="space-between"
            >
              <Grid
                item
                zeroMinWidth
                className={classes.titleContainer}
              >
                <Clamp
                  variant="body2"
                  lines={1}
                >
                  {title}
                </Clamp>
              </Grid>

              <Grid item>
                <Typography
                  variant="body1"
                  color="textSecondary"
                >
                  <FavoriteIcon
                    className={classes.favoriteIcon}
                    color="error"
                  />

                  <span>
                    {getRatingString(score, numReviews)}
                  </span>
                </Typography>
              </Grid>
            </Grid>

            <Grid
              item
              container
              spacing={1}
              justifyContent="space-between"
            >
              <Grid
                item
                zeroMinWidth
                className={classes.titleContainer}
              >
                <Clamp lines={1}>
                  {location}

                  {Boolean(location) && ` ${INTERPUNCT} `}

                  {dateRange}
                </Clamp>
              </Grid>

              {Boolean(spotifyMonthlyActiveListeners) && (
                <Grid item>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                  >
                    <FontAwesomeIcon
                      icon={faSpotify}
                      color="success"
                      className={classes.favoriteIcon}
                    />

                    <span>
                      {spotifyMonthlyActiveListeners}
                    </span>
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </div>
      </CardActionArea>
    </Card>
  );
});
